import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getMarketingEmailTemplates } from 'services/CampaignsService';

const DEFAULT_PAGE_SIZE = 20;

/**
 * Marketing email templates list: GET `/mailing/v2/template` via `CampaignsService.getMarketingEmailTemplates`.
 * Name search is server-side (`name` query param). Paging is client-side over the fetched page.
 *
 * @param {object} options
 * @param {string} [options.searchQuery]
 * @param {number} [options.pageSize]
 */
export function useMarketingTemplates({ searchQuery = '', pageSize = DEFAULT_PAGE_SIZE } = {}) {
    const [sourceTemplates, setSourceTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [visibleCount, setVisibleCount] = useState(pageSize);

    const requestGenerationRef = useRef(0);

    const normalizedSearchQuery = (searchQuery || '').trim();

    useEffect(() => {
        setVisibleCount(pageSize);
    }, [normalizedSearchQuery, pageSize]);

    const templates = useMemo(
        () => sourceTemplates.slice(0, visibleCount),
        [sourceTemplates, visibleCount],
    );

    const hasMore = visibleCount < sourceTemplates.length;

    const loadMore = useCallback(() => {
        setVisibleCount((c) => Math.min(c + pageSize, sourceTemplates.length));
    }, [sourceTemplates.length, pageSize]);

    const loadInitial = useCallback(async () => {
        const generation = ++requestGenerationRef.current;
        setError(null);
        setIsLoading(true);

        try {
            const data = await getMarketingEmailTemplates(normalizedSearchQuery);
            if (generation !== requestGenerationRef.current) {
                return;
            }
            setSourceTemplates(Array.isArray(data) ? data : []);
            setVisibleCount(pageSize);
            setError(null);
        } catch (e) {
            if (generation !== requestGenerationRef.current) {
                return;
            }
            setSourceTemplates([]);
            setError(e);
        } finally {
            if (generation === requestGenerationRef.current) {
                setIsLoading(false);
            }
        }
    }, [normalizedSearchQuery, pageSize]);

    useEffect(() => {
        loadInitial();
    }, [loadInitial]);

    const refetch = useCallback(() => {
        loadInitial();
    }, [loadInitial]);

    return {
        templates,
        isLoading,
        error,
        hasMore,
        loadMore,
        refetch,
    };
}
